/**
 * CP-90: Disney / Cunard / Viking shells are not shoppable peers.
 *
 * A shell is unpublished when that line has no departure date, or no real
 * ship. Browse groups those rows under "Schedule not yet published".
 * Recommendations drop placeholder names, 0-night rows, departed sailings,
 * and Same Ship matches when the ship is unknown. Nothing here invents a
 * port, a date, a ship, or a fare.
 */

export const SCHEDULE_UNPUBLISHED_LABEL = 'Schedule not yet published';

const SHELL_LINES = new Set([
  'disney cruise line',
  'cunard line',
  'viking cruises',
]);

const UNKNOWN_SHIPS = new Set([
  '',
  'ship not specified',
  'unknown ship',
  'unknown',
  'n/a',
  'na',
  'none',
  'null',
  'tbd',
  '-',
  '—',
]);

const GARBAGE_NAMES = new Set([
  '',
  'unknown cruise',
  'unknown',
  'n/a',
  'na',
  'none',
  'null',
  'tbd',
  'cruise',
]);

export function isShellCruiseLine(cruiseLine) {
  return SHELL_LINES.has(String(cruiseLine || '').trim().toLowerCase());
}

export function isShipUnknown(cruise) {
  if (!cruise) return true;
  if (cruise.ship_name_unmapped === true) return true;
  const name = String(cruise.ship_name || '').trim().toLowerCase();
  return UNKNOWN_SHIPS.has(name);
}

export function isUnpublishedShell(cruise) {
  if (!cruise || !isShellCruiseLine(cruise.cruise_line)) return false;
  const noDate = !String(cruise.departure_date || '').trim();
  return noDate || isShipUnknown(cruise);
}

/**
 * Detail From / cabin widgets quote a fare only when the caller already
 * accepts it (fresh lead-in) and the sailing is not an unpublished shell.
 * A stored current_price on an undated or unmapped Disney/Cunard/Viking
 * row is not a shoppable price. Dated sailings keep the caller's decision.
 */
export function showsShoppableFare(cruise, fareIsDisplayable) {
  if (isUnpublishedShell(cruise)) return false;
  return Boolean(fareIsDisplayable);
}

function calendarDay(value) {
  if (value == null || value === '') return '';
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function todayUtcDay(now) {
  const date = now instanceof Date ? now : new Date();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

/** A recommendation card we will not render (placeholder, 0 nights, past, undated, shell). */
export function isHonestRecommendation(cruise, now) {
  if (!cruise || isUnpublishedShell(cruise)) return false;
  const name = String(cruise.name || '').trim().toLowerCase();
  if (!name || GARBAGE_NAMES.has(name)) return false;
  const nights = Number(cruise.duration);
  if (!Number.isFinite(nights) || nights <= 0) return false;
  const day = calendarDay(cruise.departure_date);
  if (!day || day < todayUtcDay(now)) return false;
  return true;
}

export function showSameShipBadge(current, other) {
  if (!current || !other) return false;
  if (isShipUnknown(current) || isShipUnknown(other)) return false;
  const left = String(current.ship_name || '').trim();
  const right = String(other.ship_name || '').trim();
  return Boolean(left) && left === right;
}

/**
 * Hide Same Ship recommendations when this sailing's ship is unknown.
 * Other honest sailings on a known ship can still appear.
 */
export function includeRecommendation(current, other, now) {
  if (!isHonestRecommendation(other, now)) return false;
  if (isShipUnknown(current) && isShipUnknown(other)) return false;
  return true;
}

function recommendationRegionKey(cruise) {
  if (!cruise) return '';
  const id = cruise.destination_id;
  if (typeof id === 'string' && id.trim()) return `id:${id.trim().toLowerCase()}`;
  const raw = String(cruise.destination_raw || cruise.destination || '').trim().toLowerCase();
  return raw ? `raw:${raw}` : '';
}

function sameRecommendationRegion(current, other) {
  const left = recommendationRegionKey(current);
  const right = recommendationRegionKey(other);
  return Boolean(left) && left === right;
}

/**
 * CP-131: same region first, then fresh fares. A "Price unavailable" card
 * never leads inside its region when a fare exists. Order inside each
 * group stays as the caller (API) sent it.
 */
export function orderRecommendationCards(cruises, showsPrice, currentCruise) {
  const regionMatched = [];
  const regionOther = [];
  for (const cruise of cruises || []) {
    if (currentCruise && sameRecommendationRegion(currentCruise, cruise)) {
      regionMatched.push(cruise);
    } else {
      regionOther.push(cruise);
    }
  }
  // When the caller omitted currentCruise, keep the CP-92 fare-first behavior
  // on the full list (everything lands in regionOther).
  const groups = currentCruise ? [regionMatched, regionOther] : [regionOther];
  const ordered = [];
  for (const group of groups) {
    const priced = [];
    const unavailable = [];
    for (const cruise of group) {
      if (typeof showsPrice === 'function' && showsPrice(cruise)) priced.push(cruise);
      else unavailable.push(cruise);
    }
    ordered.push(...priced, ...unavailable);
  }
  return ordered;
}
